import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Phone, Mail, Building2, Briefcase, Cake, MapPin,
  Calendar, Clock, Pencil, ClipboardList, Repeat, ArrowRight,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { Chip } from "@/components/ui-bits";

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]?.toUpperCase()).join("");
}
import { avatarBg } from "@/lib/roleColors";
function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmtHours(h) {
  const n = Number(h) || 0;
  return Number.isInteger(n) ? `${n}u` : `${n.toFixed(1)}u`;
}
function shiftHours(a, b) {
  if (!a || !b) return 0;
  const [ah, am] = a.split(":").map(Number); const [bh, bm] = b.split(":").map(Number);
  let m = (bh * 60 + bm) - (ah * 60 + am); if (m < 0) m += 1440;
  return Math.round((m / 60) * 10) / 10;
}
function shortBirthday(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("nl", { day: "numeric", month: "long" });
}
function shortDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("nl", { day: "numeric", month: "short", year: "numeric" });
}

const STATUS_VARIANT = {
  pending: "warning",
  accepted: "warning",
  approved: "success",
  rejected: "danger",
  declined: "danger",
  cancelled: "neutral",
};

export default function EmployeeDetail({ employee, onClose, onEdit, locations = [], teams = [] }) {
  const { t } = useI18n();
  const [tab, setTab] = useState("overview");
  const [shifts, setShifts] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employee) return;
    (async () => {
      setLoading(true);
      try {
        const today = new Date();
        const start = fmtDate(today);
        const end = fmtDate(new Date(today.getTime() + 60 * 86400000));
        const [sRes, lRes, wRes] = await Promise.all([
          api.get(`/shifts?start=${start}&end=${end}`),
          api.get(`/leave-requests?employee_id=${employee._id}`).catch(() => ({ data: [] })),
          api.get(`/swap-requests`).catch(() => ({ data: [] })),
        ]);
        setShifts((sRes.data || []).filter((s) => s.employee_id === employee._id));
        setLeaves(lRes.data || []);
        setSwaps((wRes.data || []).filter(
          (w) => w.from_employee_id === employee._id || w.target_employee_id === employee._id
        ));
      } finally {
        setLoading(false);
      }
    })();
  }, [employee]);

  const locName = (id) => locations.find((l) => l._id === id)?.name || "—";
  const locColor = (id) => locations.find((l) => l._id === id)?.color || "#2563EB";
  const teamName = (id) => teams.find((tt) => tt._id === id)?.name || "—";

  const upcoming = useMemo(() => {
    const today = fmtDate(new Date());
    return shifts.filter((s) => s.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  }, [shifts]);

  const totalUpcomingHours = upcoming.reduce((sum, s) => sum + shiftHours(s.start_time, s.end_time), 0);

  const pendingLeaveCount = useMemo(
    () => leaves.filter((l) => l.status === "pending").length,
    [leaves]
  );
  const pendingSwapCount = useMemo(
    () => swaps.filter((w) => ["pending", "accepted"].includes(w.status)).length,
    [swaps]
  );

  if (!employee) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
        onClick={onClose}
        data-testid="emp-detail-modal"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="card w-full max-w-2xl overflow-hidden max-h-[92vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          style={{ boxShadow: "var(--shadow-xl)" }}
        >
          {/* Header with avatar + name */}
          <div className="p-6 border-b hairline flex items-start gap-4">
            <div
              className="w-16 h-16 rounded-2xl grid place-items-center text-white font-display text-[22px] font-bold shrink-0"
              style={{ background: avatarBg(employee.name, employee.role) }}
              data-testid="emp-detail-avatar"
            >
              {initials(employee.name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="eyebrow">{t(`role_${employee.role}`)}</div>
              <h3 className="font-display text-[24px] tracking-tight text-[hsl(var(--text-strong))] mt-0.5 truncate">
                {employee.name}
              </h3>
              {employee.job_title && (
                <div className="text-[13px] text-[hsl(var(--text-muted))] mt-0.5 flex items-center gap-1.5">
                  <Briefcase size={12} strokeWidth={2} /> {employee.job_title}
                  {employee.department && <span className="text-[hsl(var(--text-muted))]"> · {employee.department}</span>}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {onEdit && (
                <button onClick={onEdit} className="btn btn-ghost !h-9" data-testid="emp-detail-edit">
                  <Pencil size={13} /> {t("edit")}
                </button>
              )}
              <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))]" data-testid="emp-detail-close">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6 pt-3 border-b hairline flex items-center gap-1">
            <TabButton
              active={tab === "overview"} onClick={() => setTab("overview")}
              testid="emp-tab-overview" Icon={Calendar} label={t("emp_tab_overview")}
            />
            <TabButton
              active={tab === "leave"} onClick={() => setTab("leave")}
              testid="emp-tab-leave" Icon={ClipboardList} label={t("emp_tab_leave")}
              badge={pendingLeaveCount}
            />
            <TabButton
              active={tab === "swap"} onClick={() => setTab("swap")}
              testid="emp-tab-swap" Icon={Repeat} label={t("emp_tab_swap")}
              badge={pendingSwapCount}
            />
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1">
            {tab === "overview" && (
              <>
                {/* Contact info grid */}
                <div className="p-6 pb-3">
                  <div className="eyebrow mb-3">{t("contact_info")}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    <InfoRow Icon={Mail} label={t("email")} value={employee.email} testid="emp-detail-email" copyable />
                    <InfoRow Icon={Phone} label={t("phone")} value={employee.phone || "—"} testid="emp-detail-phone" copyable={!!employee.phone} />
                    <InfoRow Icon={MapPin} label={t("location")} value={locName(employee.location_id)} testid="emp-detail-location" />
                    <InfoRow Icon={Building2} label={t("team")} value={teamName(employee.team_id)} testid="emp-detail-team" />
                    {employee.date_of_birth && (
                      <InfoRow Icon={Cake} label={t("birthday_col")} value={shortBirthday(employee.date_of_birth)} testid="emp-detail-birthday" />
                    )}
                  </div>
                </div>

                {/* Upcoming shifts */}
                <div className="p-6 pt-3 border-t hairline">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="eyebrow flex items-center gap-1.5">
                        <Calendar size={11} strokeWidth={2} /> {t("upcoming_shifts")}
                      </span>
                      {upcoming.length > 0 && (
                        <span className="text-[11px] text-[hsl(var(--text-muted))]">
                          · {upcoming.length} {t("shifts_selected")} · <b className="text-[hsl(var(--primary))]">{fmtHours(totalUpcomingHours)}</b>
                        </span>
                      )}
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-[13px] text-[hsl(var(--text-muted))] text-center py-6">{t("checking")}</div>
                  ) : upcoming.length === 0 ? (
                    <EmptyBlock Icon={Calendar} title={t("no_upcoming_shifts")} hint={t("no_upcoming_shifts_hint")} />
                  ) : (
                    <div className="flex flex-col gap-1.5" data-testid="emp-detail-shifts">
                      {upcoming.map((s) => {
                        const dur = shiftHours(s.start_time, s.end_time);
                        return (
                          <div
                            key={s._id}
                            className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] p-2.5 pl-3"
                            style={{ borderLeft: `4px solid ${locColor(s.location_id)}` }}
                            data-testid={`emp-detail-shift-${s._id}`}
                          >
                            <div className="w-11 text-center shrink-0">
                              <div className="text-[9px] uppercase tracking-widest font-bold text-[hsl(var(--text-muted))]">
                                {new Date(s.date).toLocaleDateString("nl", { weekday: "short" })}
                              </div>
                              <div className="font-display text-[16px] tabular-nums font-bold text-[hsl(var(--text-strong))] leading-none mt-0.5">
                                {new Date(s.date).getDate()}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-[12px] font-semibold tabular-nums text-[hsl(var(--text-strong))]">
                                  {s.start_time}–{s.end_time}
                                </span>
                                <span className="text-[10px] font-bold tabular-nums text-[hsl(var(--primary))] bg-[hsl(var(--primary-soft))] px-1.5 py-0.5 rounded-md">
                                  {fmtHours(dur)}
                                </span>
                                <Chip variant={s.status === "published" ? "success" : "warning"}>{t(s.status)}</Chip>
                              </div>
                              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5 flex items-center gap-2 truncate">
                                {s.role_label && <span className="truncate">{s.role_label}</span>}
                                {s.location_id && <span className="flex items-center gap-1 shrink-0"><MapPin size={10} /> {locName(s.location_id)}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {tab === "leave" && (
              <div className="p-6">
                {loading ? (
                  <div className="text-[13px] text-[hsl(var(--text-muted))] text-center py-6">{t("checking")}</div>
                ) : leaves.length === 0 ? (
                  <EmptyBlock Icon={ClipboardList} title={t("emp_no_leave_title")} hint={t("emp_no_leave_hint")} />
                ) : (
                  <div className="flex flex-col gap-2" data-testid="emp-detail-leaves">
                    {leaves.map((l) => (
                      <div
                        key={l._id}
                        className="rounded-xl border border-[hsl(var(--border))] p-3"
                        data-testid={`emp-detail-leave-${l._id}`}
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary-soft))] grid place-items-center shrink-0">
                              <ClipboardList size={13} className="text-[hsl(var(--primary))]" />
                            </div>
                            <div>
                              <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] leading-tight">
                                {t(l.leave_type || l.type || "vacation")}
                              </div>
                              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5 flex items-center gap-1">
                                <Calendar size={10} />
                                {shortDate(l.start_date)}
                                <ArrowRight size={9} className="opacity-60" />
                                {shortDate(l.end_date)}
                              </div>
                            </div>
                          </div>
                          <Chip variant={STATUS_VARIANT[l.status] || "neutral"}>{t(l.status)}</Chip>
                        </div>
                        {l.reason && (
                          <div className="mt-2 pl-10 text-[12px] text-[hsl(var(--text-muted))] leading-snug">
                            {l.reason}
                          </div>
                        )}
                        {l.manager_note && (
                          <div className="mt-1.5 pl-10 text-[11px] text-[hsl(var(--text-muted))] italic leading-snug">
                            {t("manager_note")}: {l.manager_note}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "swap" && (
              <div className="p-6">
                {loading ? (
                  <div className="text-[13px] text-[hsl(var(--text-muted))] text-center py-6">{t("checking")}</div>
                ) : swaps.length === 0 ? (
                  <EmptyBlock Icon={Repeat} title={t("emp_no_swap_title")} hint={t("emp_no_swap_hint")} />
                ) : (
                  <div className="flex flex-col gap-2" data-testid="emp-detail-swaps">
                    {swaps.map((w) => {
                      const isFrom = w.from_employee_id === employee._id;
                      return (
                        <div
                          key={w._id}
                          className="rounded-xl border border-[hsl(var(--border))] p-3"
                          data-testid={`emp-detail-swap-${w._id}`}
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary-soft))] grid place-items-center shrink-0">
                                <Repeat size={13} className="text-[hsl(var(--primary))]" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] leading-tight flex items-center gap-1.5">
                                  <Clock size={11} className="opacity-70" />
                                  <span className="tabular-nums">{w.shift_summary || "—"}</span>
                                </div>
                                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5 truncate">
                                  {isFrom ? (
                                    <>
                                      {t("emp_swap_from")}: <b>{w.from_employee_name}</b>
                                      {w.target_employee_name && <> → {w.target_employee_name}</>}
                                    </>
                                  ) : (
                                    <>
                                      {t("emp_swap_to")}: <b>{w.target_employee_name || employee.name}</b>
                                      {w.from_employee_name && <> ← {w.from_employee_name}</>}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Chip variant={STATUS_VARIANT[w.status] || "neutral"}>{t(w.status)}</Chip>
                          </div>
                          {w.message && (
                            <div className="mt-2 pl-10 text-[12px] text-[hsl(var(--text-muted))] leading-snug">
                              “{w.message}”
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function TabButton({ active, onClick, Icon, label, badge, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative flex items-center gap-1.5 px-3 h-10 text-[12.5px] font-semibold transition-colors ${
        active
          ? "text-[hsl(var(--text-strong))]"
          : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-strong))]"
      }`}
    >
      <Icon size={13} strokeWidth={2.2} />
      {label}
      {badge > 0 && (
        <span className="ml-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold grid place-items-center bg-[hsl(var(--primary))] text-white">
          {badge}
        </span>
      )}
      {active && (
        <motion.span
          layoutId="emp-tab-indicator"
          className="absolute left-2 right-2 -bottom-px h-[2px] rounded-full"
          style={{ background: "hsl(var(--primary))" }}
        />
      )}
    </button>
  );
}

function EmptyBlock({ Icon, title, hint }) {
  return (
    <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-6 text-center">
      <Icon size={20} className="mx-auto text-[hsl(var(--text-muted))] mb-2" />
      <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{title}</div>
      <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">{hint}</div>
    </div>
  );
}

function InfoRow({ Icon, label, value, testid, copyable }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    if (!copyable || !value || value === "—") return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };
  return (
    <div
      onClick={doCopy}
      className={`flex items-center gap-2.5 p-2.5 rounded-xl bg-[hsl(var(--muted))] ${copyable && value && value !== "—" ? "cursor-pointer hover:bg-[hsl(var(--primary-soft))] transition-colors" : ""}`}
      data-testid={testid}
      title={copyable && value !== "—" ? "Klik om te kopiëren" : undefined}
    >
      <div className="w-7 h-7 rounded-lg bg-[hsl(var(--surface))] grid place-items-center shrink-0">
        <Icon size={13} strokeWidth={2} className="text-[hsl(var(--primary))]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--text-muted))]">{label}</div>
        <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] truncate">{copied ? "✓ Gekopieerd" : value}</div>
      </div>
    </div>
  );
}
